import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scene } from '../../types';
import { AssetField } from './AssetPickerModal';
import { assetUrl } from './scenario-api';
import { PedaCategorySelect } from './PedaCategorySelect';
import {
  applyClickableScreenScene,
  ClickableScreenEditorConfig,
  ClickableZoneConfig,
  parseClickableScreenScene,
} from './clickable-screen-editor-model';
import { uniqueId } from './utils';
import styles from './scenario-studio.module.css';

interface ClickableScreenEditorProps {
  scenarioId: string;
  scene: Scene;
  sceneIds: string[];
  onChange: (scene: Scene) => void;
}

function defaultZone(): ClickableZoneConfig {
  const id = uniqueId('zone');
  return {
    id,
    label: 'Zone',
    position: { x: 50, y: 50, width: 20, height: 8 },
    goto: '',
    pedaShields: 0,
    pedaCategory: 'hameconnage',
    condition: '',
    actions: [],
  };
}

export const ClickableScreenEditor: React.FC<ClickableScreenEditorProps> = ({
  scenarioId,
  scene,
  sceneIds,
  onChange,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const config = useMemo(() => parseClickableScreenScene(scene), [scene]);
  const selected = config.zones.find((zone) => zone.id === selectedId) ?? null;

  const commit = useCallback(
    (nextConfig: ClickableScreenEditorConfig) => {
      onChange(applyClickableScreenScene(scene, nextConfig));
    },
    [onChange, scene],
  );

  const patchConfig = (patch: Partial<ClickableScreenEditorConfig>) => {
    commit({ ...config, ...patch });
  };

  const patchZone = (id: string, patch: Partial<ClickableZoneConfig>) => {
    commit({
      ...config,
      zones: config.zones.map((zone) => (zone.id === id ? { ...zone, ...patch } : zone)),
    });
  };

  const addZone = () => {
    const zone = defaultZone();
    commit({ ...config, zones: [...config.zones, zone] });
    setSelectedId(zone.id);
  };

  const removeZone = (id: string) => {
    commit({ ...config, zones: config.zones.filter((zone) => zone.id !== id) });
    if (selectedId === id) setSelectedId(null);
  };

  const updatePositionFromPointer = useCallback(
    (zoneId: string, clientX: number, clientY: number) => {
      const preview = previewRef.current;
      if (!preview) return;
      const rect = preview.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((clientX - rect.left) / rect.width) * 1000) / 10));
      const y = Math.min(100, Math.max(0, Math.round(((clientY - rect.top) / rect.height) * 1000) / 10));
      const current = parseClickableScreenScene(scene);
      const target = current.zones.find((zone) => zone.id === zoneId);
      if (!target) return;
      commit({
        ...current,
        zones: current.zones.map((zone) =>
          zone.id === zoneId ? { ...zone, position: { ...zone.position, x, y } } : zone
        ),
      });
    },
    [commit, scene],
  );

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: MouseEvent) => updatePositionFromPointer(draggingId, e.clientX, e.clientY);
    const onUp = () => setDraggingId(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, updatePositionFromPointer]);

  return (
    <div className={styles.objectInspectEditor}>
      <div className={styles.panelHeader}>
        <h3>Écran cliquable</h3>
        <span className={styles.inspectSummary}>
          {config.zones.length} zone{config.zones.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className={styles.inspectSceneSettings}>
        <div className={styles.field}>
          <label>Pensée du personnage (instruction)</label>
          <textarea
            className={styles.textarea}
            rows={2}
            value={config.instruction}
            onChange={(e) => patchConfig({ instruction: e.target.value })}
            placeholder="Ex. : Le portail me demande mon compte du ministère."
          />
        </div>
        <AssetField
          scenarioId={scenarioId}
          label="Fond de scène"
          value={scene.background ?? ''}
          onChange={(background) => onChange({ ...scene, background } as Scene)}
        />
      </div>

      <div className={styles.elementsLayout}>
        <div
          ref={previewRef}
          className={styles.scenePreview}
          style={{
            backgroundImage: scene.background
              ? `url(${assetUrl(scenarioId, scene.background)})`
              : undefined,
          }}
        >
          {config.zones.map((zone) => (
            <div
              key={zone.id}
              className={`${styles.previewElement} ${styles.inspectPreviewElement} ${selectedId === zone.id ? styles.selected : ''}`}
              style={{
                left: `${zone.position.x}%`,
                top: `${zone.position.y}%`,
                width: `${zone.position.width}%`,
                height: `${zone.position.height}%`,
                transform: 'translate(-50%, -50%)',
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                setSelectedId(zone.id);
                setDraggingId(zone.id);
              }}
            >
              <span className={styles.elementLabel}>{zone.label}</span>
            </div>
          ))}
        </div>

        <div className={styles.elementsSidebar}>
          <div className={styles.row}>
            <button type="button" className={styles.btnSecondary} onClick={addZone}>
              + Zone
            </button>
          </div>

          {config.zones.map((zone) => (
            <button
              key={zone.id}
              type="button"
              className={`${styles.listItem} ${selectedId === zone.id ? styles.active : ''}`}
              onClick={() => setSelectedId(zone.id)}
            >
              {zone.label || zone.id}
            </button>
          ))}

          {selected && (
            <div className={styles.card}>
              <div className={styles.field}>
                <label>ID</label>
                <input
                  className={styles.input}
                  value={selected.id}
                  onChange={(e) => {
                    const newId = e.target.value;
                    commit({
                      ...config,
                      zones: config.zones.map((zone) =>
                        zone.id === selected.id ? { ...zone, id: newId } : zone
                      ),
                    });
                    setSelectedId(newId);
                  }}
                />
              </div>
              <div className={styles.field}>
                <label>Libellé</label>
                <input
                  className={styles.input}
                  value={selected.label}
                  onChange={(e) => patchZone(selected.id, { label: e.target.value })}
                />
              </div>
              <div className={styles.inspectSettingsRow}>
                <div className={styles.field}>
                  <label>Largeur %</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={selected.position.width}
                    onChange={(e) =>
                      patchZone(selected.id, {
                        position: {
                          ...selected.position,
                          width: parseFloat(e.target.value) || 1,
                        },
                      })
                    }
                  />
                </div>
                <div className={styles.field}>
                  <label>Hauteur %</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={selected.position.height}
                    onChange={(e) =>
                      patchZone(selected.id, {
                        position: {
                          ...selected.position,
                          height: parseFloat(e.target.value) || 1,
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className={styles.field}>
                <label>Scène cible (goto)</label>
                <select
                  className={styles.select}
                  value={selected.goto}
                  onChange={(e) => patchZone(selected.id, { goto: e.target.value })}
                >
                  <option value="">— Choisir —</option>
                  {sceneIds.filter((id) => id !== scene.id).map((id) => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>Condition (optionnel)</label>
                <input
                  className={styles.input}
                  value={selected.condition}
                  onChange={(e) => patchZone(selected.id, { condition: e.target.value })}
                  placeholder="flag:exemple"
                />
              </div>
              <div className={styles.inspectSettingsRow}>
                <div className={styles.field}>
                  <label>pedaShields</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={selected.pedaShields}
                    onChange={(e) =>
                      patchZone(selected.id, {
                        pedaShields: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </div>
                {selected.pedaShields > 0 && (
                  <div className={styles.field}>
                    <label>pedaCategory</label>
                    <PedaCategorySelect
                      value={selected.pedaCategory}
                      onChange={(pedaCategory) => patchZone(selected.id, { pedaCategory })}
                    />
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={() => removeZone(selected.id)}
              >
                Supprimer la zone
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
